import { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../constants/api";
import {
  DOCTOR_STEPS,
  DOCTOR_STEP_DESCRIPTIONS,
  DOCTOR_MENU_GROUPS,
  getGroupSteps,
} from "../constants/menuConfig";
import { buildDoctorFormData, parseApiError } from "../utils/helpers";
import {
  mapDoctorToProfileForm,
  mergeDoctorProfileFormData,
  hasDoctorProfileData,
} from "../utils/doctorFormHelpers";
import { getStoredToken, getStoredUser, syncStoredUser } from "../auth/authStorage";
import { useAuth } from "./AuthContext";

const loadJson = async (url) => {
  try {
    const r = await fetch(url);
    if (!r.ok) return [];
    const data = await r.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
};

export function useDoctorPortalState() {
  const { user: authUser } = useAuth();
  const [portalUser, setPortalUser] = useState(authUser);
  const [currentStep, setCurrentStep] = useState(0);
  const [profileView, setProfileView] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ "doctor-menu": true });
  const [expandedSections, setExpandedSections] = useState({});
  const [doctor, setDoctor] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [medicalProcedures, setMedicalProcedures] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [accreditations, setAccreditations] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [profileForm, setProfileForm] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [mediaGallery, setMediaGallery] = useState([]);
  const [mediaGalleryLoading, setMediaGalleryLoading] = useState(false);
  const [mediaGallerySaving, setMediaGallerySaving] = useState(false);
  const [awardsRecognition, setAwardsRecognition] = useState([]);
  const [awardsLoading, setAwardsLoading] = useState(false);
  const [awardsSaving, setAwardsSaving] = useState(false);
  const [newsArticles, setNewsArticles] = useState([]);
  const [newsArticlesLoading, setNewsArticlesLoading] = useState(false);
  const [newsArticlesSaving, setNewsArticlesSaving] = useState(false);
  const [digitalCme, setDigitalCme] = useState([]);
  const [digitalCmeLoading, setDigitalCmeLoading] = useState(false);
  const [digitalCmeSaving, setDigitalCmeSaving] = useState(false);
  const [medicalSpecialties, setMedicalSpecialties] = useState([]);

  const doctorId = portalUser?.doctor_id ?? authUser?.doctor_id;

  const resolvePortalUser = useCallback(async () => {
    let currentUser = authUser || getStoredUser();
    if (!currentUser) return null;

    if (currentUser.doctor_id) {
      setPortalUser(currentUser);
      return currentUser;
    }

    const token = getStoredToken();
    if (token) {
      try {
        const r = await fetch(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const data = await r.json();
          if (data.user) {
            setPortalUser(data.user);
            syncStoredUser(data.user);
            return data.user;
          }
        }
      } catch {
        /* use cached user */
      }
    }

    if (currentUser.login_id || currentUser.mobile_no) {
      try {
        const r = await fetch(`${API_BASE}/master/logins`);
        if (r.ok) {
          const logins = await r.json();
          const match = logins.find(
            (l) =>
              (currentUser.login_id && String(l.login_id) === String(currentUser.login_id)) ||
              (currentUser.mobile_no && l.mobile_no === currentUser.mobile_no)
          );
          if (match?.doctor_id) {
            const updated = {
              ...currentUser,
              login_id: match.login_id,
              doctor_id: match.doctor_id,
              user_name: match.user_name ?? currentUser.user_name,
              email_address: match.email_address ?? currentUser.email_address,
            };
            setPortalUser(updated);
            syncStoredUser(updated);
            return updated;
          }
        }
      } catch {
        return currentUser;
      }
    }

    setPortalUser(currentUser);
    return currentUser;
  }, [authUser]);

  const fetchDoctor = useCallback(async (id) => {
    if (!id) return null;
    const r = await fetch(`${API_BASE}/master/doctors/${id}`);
    if (!r.ok) throw new Error(await parseApiError(r));
    return r.json();
  }, []);

  const fetchMediaGallery = useCallback(async (docId = doctorId) => {
    if (!docId) {
      setMediaGallery([]);
      return [];
    }
    setMediaGalleryLoading(true);
    try {
      const r = await fetch(`${API_BASE}/master/media-gallery?doctor_id=${docId}`);
      if (!r.ok) {
        setMediaGallery([]);
        return [];
      }
      const data = await r.json();
      const rows = Array.isArray(data) ? data : [];
      setMediaGallery(rows);
      return rows;
    } catch {
      setMediaGallery([]);
      return [];
    } finally {
      setMediaGalleryLoading(false);
    }
  }, [doctorId]);

  const fetchAwardsRecognition = useCallback(async (docId = doctorId) => {
    if (!docId) {
      setAwardsRecognition([]);
      return [];
    }
    setAwardsLoading(true);
    try {
      const r = await fetch(`${API_BASE}/master/awards-recognition?doctor_id=${docId}`);
      if (!r.ok) {
        setAwardsRecognition([]);
        return [];
      }
      const data = await r.json();
      const rows = Array.isArray(data) ? data : [];
      setAwardsRecognition(rows);
      return rows;
    } catch {
      setAwardsRecognition([]);
      return [];
    } finally {
      setAwardsLoading(false);
    }
  }, [doctorId]);

  const fetchNewsArticles = useCallback(async (docId = doctorId) => {
    if (!docId) {
      setNewsArticles([]);
      return [];
    }
    setNewsArticlesLoading(true);
    try {
      const r = await fetch(`${API_BASE}/master/news-articles?doctor_id=${docId}`);
      if (!r.ok) {
        setNewsArticles([]);
        return [];
      }
      const data = await r.json();
      const rows = Array.isArray(data) ? data : [];
      setNewsArticles(rows);
      return rows;
    } catch {
      setNewsArticles([]);
      return [];
    } finally {
      setNewsArticlesLoading(false);
    }
  }, [doctorId]);

  const fetchDigitalCme = useCallback(async (docId = doctorId) => {
    if (!docId) {
      setDigitalCme([]);
      return [];
    }
    setDigitalCmeLoading(true);
    try {
      const r = await fetch(`${API_BASE}/master/digital-cme?doctor_id=${docId}`);
      if (!r.ok) {
        setDigitalCme([]);
        return [];
      }
      const data = await r.json();
      const rows = Array.isArray(data) ? data : [];
      setDigitalCme(rows);
      return rows;
    } catch {
      setDigitalCme([]);
      return [];
    } finally {
      setDigitalCmeLoading(false);
    }
  }, [doctorId]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      setLoading(true);
      try {
        const user = await resolvePortalUser();
        const id = user?.doctor_id;
        if (!id) {
          setProfileMsg("Error: No doctor profile linked to this login.");
          setLoading(false);
          return;
        }

        const [
          doc,
          specs,
          procs,
          specz,
          depts,
          desigs,
          degs,
          accrs,
          ests,
          types,
          locTypes,
          locs,
        ] = await Promise.all([
          fetchDoctor(id),
          loadJson(`${API_BASE}/master/specialties`),
          loadJson(`${API_BASE}/master/medical-procedures`),
          loadJson(`${API_BASE}/master/specializations`),
          loadJson(`${API_BASE}/master/departments`),
          loadJson(`${API_BASE}/master/designations`),
          loadJson(`${API_BASE}/master/degrees`),
          loadJson(`${API_BASE}/master/accreditations`),
          loadJson(`${API_BASE}/master/establishments`),
          loadJson(`${API_BASE}/master/user-types`),
          loadJson(`${API_BASE}/locations/types`),
          loadJson(`${API_BASE}/locations`),
        ]);

        if (cancelled) return;
        setDoctor(doc);
        setSpecialties(specs);
        setMedicalProcedures(procs);
        setSpecializations(specz);
        setDepartments(depts);
        setDesignations(desigs);
        setDegrees(degs);
        setAccreditations(accrs);
        setEstablishments(ests);
        setUserTypes(types);
        setLocationTypes(locTypes);
        setLocations(locs);
        setProfileForm(mapDoctorToProfileForm(doc));
        setProfileView("overview");

        await Promise.all([
          fetchMediaGallery(id),
          fetchAwardsRecognition(id),
          fetchNewsArticles(id),
          fetchDigitalCme(id),
        ]);
      } catch (e) {
        if (!cancelled) setProfileMsg(`Error: ${e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    loadJson(`${API_BASE}/master/specialties`).then(setMedicalSpecialties);
    return () => { cancelled = true; };
  }, [
    resolvePortalUser,
    fetchDoctor,
    fetchMediaGallery,
    fetchAwardsRecognition,
    fetchNewsArticles,
    fetchDigitalCme,
  ]);

  const openCreateProfile = () => {
    setProfileForm(mapDoctorToProfileForm(doctor));
    setProfileView("create");
    setProfileMsg("");
  };

  const closeCreateProfile = () => {
    setProfileView("overview");
    setProfileMsg("");
  };

  const handleProfileChange = (field, value) => {
    setProfileForm((p) => ({ ...p, [field]: value }));
  };

  const handleSaveProfile = async () => {
    const id = profileForm.doctor_id || doctorId;
    if (!id) {
      setProfileMsg("Error: No doctor profile linked to this login.");
      return;
    }
    const payload = mergeDoctorProfileFormData(doctor, profileForm);
    if (!payload.doctor_name?.trim()) {
      setProfileMsg("Error: Doctor name is required.");
      return;
    }
    setProfileSaving(true);
    setProfileMsg("");
    try {
      const fd = buildDoctorFormData(payload);
      const r = await fetch(`${API_BASE}/master/doctors/${id}`, { method: "PUT", body: fd });
      if (!r.ok) throw new Error(await parseApiError(r));
      const refreshed = await fetchDoctor(id);
      if (refreshed) {
        setDoctor(refreshed);
        setProfileForm(mapDoctorToProfileForm(refreshed));
      }
      setProfileView("overview");
      setCurrentStep(0);
      setProfileMsg("Profile saved successfully!");
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
    } finally {
      setProfileSaving(false);
    }
  };

  const saveMediaGalleryItem = async (payload, files = null, editId = null) => {
    const docId = doctorId || profileForm.doctor_id;
    if (!docId) {
      setProfileMsg("Error: No doctor profile linked to this login.");
      return false;
    }

    const fileList = Array.isArray(files) ? files : files ? [files] : [];
    if (payload.media_type === "Image" && !editId && fileList.length === 0) {
      setProfileMsg("Error: Please select at least one image.");
      return false;
    }

    setMediaGallerySaving(true);
    setProfileMsg("");
    try {
      const uploadOne = async (file, id = null) => {
        const fd = new FormData();
        fd.append("media_type", payload.media_type);
        fd.append("doctor_id", String(docId));
        if (payload.media_date) fd.append("media_date", payload.media_date);
        if (payload.place) fd.append("place", payload.place);
        if (payload.short_note) fd.append("short_note", payload.short_note);
        if (payload.media_type === "Video" && payload.media_link) {
          fd.append("media_link", payload.media_link);
        }
        if (payload.media_type === "Image" && file) {
          fd.append("media_upload", file);
        }

        const url = id
          ? `${API_BASE}/master/media-gallery/${id}`
          : `${API_BASE}/master/media-gallery`;
        const r = await fetch(url, { method: id ? "PUT" : "POST", body: fd });
        if (!r.ok) throw new Error(await parseApiError(r));
      };

      if (editId) {
        await uploadOne(fileList[0] || null, editId);
      } else if (payload.media_type === "Image") {
        for (const file of fileList) {
          await uploadOne(file);
        }
      } else {
        await uploadOne(null);
      }

      await fetchMediaGallery(docId);
      const count = payload.media_type === "Image" && !editId ? fileList.length : 1;
      setProfileMsg(
        editId
          ? "Gallery item updated."
          : count > 1
            ? `${count} images uploaded.`
            : "Gallery item added."
      );
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setMediaGallerySaving(false);
    }
  };

  const saveAwardRecognition = async (payload, files = [], editId = null, removedImageIds = []) => {
    const docId = doctorId || profileForm.doctor_id;
    if (!docId) {
      setProfileMsg("Error: No doctor profile linked to this login.");
      return false;
    }
    if (!payload.award_title?.trim()) {
      setProfileMsg("Error: Award title is required.");
      return false;
    }

    setAwardsSaving(true);
    setProfileMsg("");
    try {
      const fd = new FormData();
      fd.append("doctor_id", String(docId));
      fd.append("award_title", payload.award_title.trim());
      if (payload.awarding_body) fd.append("awarding_body", payload.awarding_body);
      if (payload.award_date) fd.append("award_date", payload.award_date);
      if (payload.place) fd.append("place", payload.place);
      if (payload.short_note) fd.append("short_note", payload.short_note);
      if (editId && removedImageIds.length) {
        fd.append("removed_image_ids", JSON.stringify(removedImageIds));
      }
      files.forEach((file) => fd.append("award_images", file));

      const url = editId
        ? `${API_BASE}/master/awards-recognition/${editId}`
        : `${API_BASE}/master/awards-recognition`;
      const r = await fetch(url, { method: editId ? "PUT" : "POST", body: fd });
      if (!r.ok) throw new Error(await parseApiError(r));

      await fetchAwardsRecognition(docId);
      setProfileMsg(editId ? "Award updated." : "Award saved.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setAwardsSaving(false);
    }
  };

  const deleteAwardRecognition = async (awardId) => {
    setAwardsSaving(true);
    setProfileMsg("");
    try {
      const r = await fetch(`${API_BASE}/master/awards-recognition/${awardId}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await parseApiError(r));
      await fetchAwardsRecognition(doctorId);
      setProfileMsg("Award deleted.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setAwardsSaving(false);
    }
  };

  const saveNewsArticle = async (
    payload,
    referenceFiles = [],
    galleryFiles = [],
    editId = null,
    removedReferenceIds = [],
    removedImageIds = []
  ) => {
    const docId = doctorId || profileForm.doctor_id;
    if (!docId) {
      setProfileMsg("Error: No doctor profile linked to this login.");
      return false;
    }
    if (!payload.article_title?.trim()) {
      setProfileMsg("Error: Article title is required.");
      return false;
    }

    setNewsArticlesSaving(true);
    setProfileMsg("");
    try {
      const fd = new FormData();
      fd.append("doctor_id", String(docId));
      fd.append("article_title", payload.article_title.trim());
      if (payload.context_tags) fd.append("context_tags", payload.context_tags);
      if (payload.media_link) fd.append("media_link", payload.media_link);
      if (payload.short_description) fd.append("short_description", payload.short_description);
      if (payload.publication_name) fd.append("publication_name", payload.publication_name);
      if (payload.publication_date) fd.append("publication_date", payload.publication_date);
      if (editId && removedReferenceIds.length) {
        fd.append("removed_reference_ids", JSON.stringify(removedReferenceIds));
      }
      if (editId && removedImageIds.length) {
        fd.append("removed_image_ids", JSON.stringify(removedImageIds));
      }
      referenceFiles.forEach((file) => fd.append("reference_pdfs", file));
      galleryFiles.forEach((file) => fd.append("gallery_images", file));

      const url = editId
        ? `${API_BASE}/master/news-articles/${editId}`
        : `${API_BASE}/master/news-articles`;
      const r = await fetch(url, { method: editId ? "PUT" : "POST", body: fd });
      if (!r.ok) throw new Error(await parseApiError(r));

      await fetchNewsArticles(docId);
      setProfileMsg(editId ? "News article updated." : "News article saved.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setNewsArticlesSaving(false);
    }
  };

  const deleteNewsArticle = async (articleId) => {
    setNewsArticlesSaving(true);
    setProfileMsg("");
    try {
      const r = await fetch(`${API_BASE}/master/news-articles/${articleId}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await parseApiError(r));
      await fetchNewsArticles(doctorId);
      setProfileMsg("News article deleted.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setNewsArticlesSaving(false);
    }
  };

  const saveDigitalCme = async (
    payload,
    referenceFiles = [],
    editId = null,
    removedReferenceIds = []
  ) => {
    const docId = doctorId || profileForm.doctor_id;
    if (!docId) {
      setProfileMsg("Error: No doctor profile linked to this login.");
      return false;
    }
    if (!payload.cme_title?.trim()) {
      setProfileMsg("Error: CME title is required.");
      return false;
    }

    setDigitalCmeSaving(true);
    setProfileMsg("");
    try {
      const specialtyIds = payload.medical_specialty_ids
        ? payload.medical_specialty_ids.split(",").map((s) => s.trim()).filter(Boolean)
        : [];

      const fd = new FormData();
      fd.append("doctor_id", String(docId));
      fd.append("cme_title", payload.cme_title.trim());
      if (payload.context_tags) fd.append("context_tags", payload.context_tags);
      if (payload.media_link) fd.append("media_link", payload.media_link);
      if (payload.short_description) fd.append("short_description", payload.short_description);
      if (payload.long_description) fd.append("long_description", payload.long_description);
      if (payload.duration_minutes) fd.append("duration_minutes", payload.duration_minutes);
      fd.append("medical_specialty_ids", JSON.stringify(specialtyIds));
      if (editId && removedReferenceIds.length) {
        fd.append("removed_reference_ids", JSON.stringify(removedReferenceIds));
      }
      referenceFiles.forEach((file) => fd.append("reference_pdfs", file));

      const url = editId
        ? `${API_BASE}/master/digital-cme/${editId}`
        : `${API_BASE}/master/digital-cme`;
      const r = await fetch(url, { method: editId ? "PUT" : "POST", body: fd });
      if (!r.ok) throw new Error(await parseApiError(r));

      await fetchDigitalCme(docId);
      setProfileMsg(editId ? "Digital CME updated." : "Digital CME saved.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setDigitalCmeSaving(false);
    }
  };

  const deleteDigitalCme = async (cmeId) => {
    setDigitalCmeSaving(true);
    setProfileMsg("");
    try {
      const r = await fetch(`${API_BASE}/master/digital-cme/${cmeId}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await parseApiError(r));
      await fetchDigitalCme(doctorId);
      setProfileMsg("Digital CME deleted.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setDigitalCmeSaving(false);
    }
  };

  const deleteMediaGalleryItem = async (mediaId) => {
    setMediaGallerySaving(true);
    setProfileMsg("");
    try {
      const r = await fetch(`${API_BASE}/master/media-gallery/${mediaId}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await parseApiError(r));
      await fetchMediaGallery(doctorId);
      setProfileMsg("Gallery item deleted.");
      return true;
    } catch (e) {
      setProfileMsg(`Error: ${e.message}`);
      return false;
    } finally {
      setMediaGallerySaving(false);
    }
  };

  const toggleMenuGroup = (groupId) => {
    const isOpen = !!expandedGroups[groupId];
    if (isOpen) {
      setExpandedGroups({});
      setExpandedSections((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([key]) => !key.startsWith(`${groupId}:`)))
      );
    } else {
      setExpandedGroups({ [groupId]: true });
      setExpandedSections({});
    }
  };

  const toggleMenuSection = (groupId, sectionId) => {
    const key = `${groupId}:${sectionId}`;
    setExpandedSections((prev) => {
      const isOpen = !!prev[key];
      if (isOpen) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { [key]: true };
    });
  };

  const selectStep = (stepIndex) => {
    setCurrentStep(stepIndex);
    setSidebarOpen(false);
    setProfileView("overview");
    const parentGroup = DOCTOR_MENU_GROUPS.find((g) => getGroupSteps(g).includes(stepIndex));
    if (parentGroup) {
      setExpandedGroups({ [parentGroup.id]: true });
    }
  };

  const updatePortalUser = useCallback((patch) => {
    setPortalUser((prev) => {
      const base = prev || authUser || {};
      const next = typeof patch === "function" ? patch(base) : { ...base, ...patch };
      syncStoredUser(next);
      return next;
    });
  }, [authUser]);

  return {
    user: portalUser,
    currentStep,
    profileView,
    STEPS: DOCTOR_STEPS,
    STEP_DESCRIPTIONS: DOCTOR_STEP_DESCRIPTIONS,
    MENU_GROUPS: DOCTOR_MENU_GROUPS,
    sidebarOpen,
    setSidebarOpen,
    expandedGroups,
    expandedSections,
    toggleMenuGroup,
    toggleMenuSection,
    selectStep,
    doctor,
    specialties,
    medicalProcedures,
    specializations,
    departments,
    designations,
    degrees,
    accreditations,
    establishments,
    userTypes,
    locationTypes,
    locations,
    profileForm,
    profileSaving,
    profileMsg,
    loading,
    openCreateProfile,
    closeCreateProfile,
    handleProfileChange,
    handleSaveProfile,
    hasDoctorProfileData: hasDoctorProfileData(doctor),
    mediaGallery,
    mediaGalleryLoading,
    mediaGallerySaving,
    saveMediaGalleryItem,
    deleteMediaGalleryItem,
    awardsRecognition,
    awardsLoading,
    awardsSaving,
    saveAwardRecognition,
    deleteAwardRecognition,
    newsArticles,
    newsArticlesLoading,
    newsArticlesSaving,
    saveNewsArticle,
    deleteNewsArticle,
    digitalCme,
    digitalCmeLoading,
    digitalCmeSaving,
    medicalSpecialties,
    saveDigitalCme,
    deleteDigitalCme,
    updatePortalUser,
  };
}
